import { PROGRESS_EPOCH } from '../../shared/progressEpoch'
import { apiFetch } from '~/utils/api'
import { IS_NATIVE } from '~/utils/platform'
import type { AuthCredential } from 'firebase/auth'
import type { Fb } from '~/composables/useFirebase'
import { cancelledSignIn, nativeAppleCredential, nativeGoogleCredential, type AppleNativeResult } from '~/utils/nativeAuth'
import { flushNativePersistence } from '~/utils/nativePersistence'

/** Plain snapshot of the signed-in player (the raw Firebase User class
 * doesn't belong in reactive state). */
export interface AuthUser {
  uid: string
  name: string
  photo: string | null
  /** 🛡️ v6.39: the lazy wallet identity (useStardust). Not a board identity. */
  anonymous: boolean
}

export function useAuthUser() {
  return useState<AuthUser | null>('auth-user', () => null)
}

/** 🍎 M7: Google everywhere; Apple on the iPhone (App Review 4.8) and, through
 * Firebase's popup, on the web. */
export type SignInMethod = 'google' | 'apple'

/** The web's popup provider. 👤 v7.88 (Javi, 13 Oct, a phone with one Google
 * session): Google reused that session without asking, so a second account
 * could not be chosen there. The chooser now always opens - for the sign-in
 * and for the guest link, which share this provider. 🍎 M7: Apple asks for
 * the name and email once, as the app's native sheet does. */
function webProvider(fb: Fb, method: SignInMethod) {
  if (method === 'apple') {
    const apple = new fb.authMod.OAuthProvider('apple.com')
    apple.addScope('email')
    apple.addScope('name')
    return apple
  }
  const google = new fb.authMod.GoogleAuthProvider()
  google.setCustomParameters({ prompt: 'select_account' })
  return google
}

/** Google sign-in (v1.16). The auth-state listener that feeds
 * useAuthUser lives in plugins/firebase.client.ts. */
export function useAuth() {
  const user = useAuthUser()
  const busy = useState('auth-busy', () => false)
  const error = useState<string | null>('auth-error', () => null)

  /** 🍎 M7 (Apple TN3194): after an iPhone's Sign in with Apple, keep what
   * deletion needs - the authorization code becomes a refresh token server-side,
   * revoked with the account - and the name Apple hands over only once. Best
   * effort: neither may hold up the sign-in. */
  async function afterAppleSignIn(fb: Fb, apple: AppleNativeResult) {
    const u = fb.auth.currentUser
    if (!u || u.isAnonymous) return
    if (apple.displayName && !u.displayName) {
      try {
        await fb.authMod.updateProfile(u, { displayName: apple.displayName })
        user.value = { uid: u.uid, name: apple.displayName, photo: u.photoURL ?? null, anonymous: false }
      } catch (cause) { console.warn('Could not keep the name Apple gave.', cause) }
    }
    if (!apple.authorizationCode) return
    try {
      const token = await u.getIdToken()
      await apiFetch('/api/auth/apple-authorize', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
        body: { code: apple.authorizationCode },
      })
    } catch (cause) { console.warn('The Apple authorization was not stored; deleting the account still works.', cause) }
  }

  async function signIn(method: SignInMethod = 'google') {
    if (!import.meta.client || busy.value) return
    busy.value = true
    error.value = null
    try {
      const fb = await bootFirebase()
      const provider = webProvider(fb, method)
      await fb.auth.authStateReady()
      // Read the guest after the account sheet: a wallet earn may have minted it
      // while the player chose a provider account.
      let apple: AppleNativeResult | null = null
      let credential: AuthCredential | null = null
      if (IS_NATIVE) {
        if (method === 'apple') { apple = await nativeAppleCredential(fb.authMod); credential = apple.credential }
        else credential = await nativeGoogleCredential(fb.authMod)
      }
      const anon = fb.auth.currentUser
      if (!anon?.isAnonymous) {
        if (credential) await fb.authMod.signInWithCredential(fb.auth, credential)
        else await fb.authMod.signInWithPopup(fb.auth, provider)
        if (apple) void afterAppleSignIn(fb, apple)
        return
      }
      try {
        // 🔗 v6.39 (launch-hardening audit): LINK the anonymous user to
        // the Google account instead of signing in as a new one. Same
        // uid afterwards, so the wallet, ownership, meta doc and cloud
        // save all stay put and nothing has to be transferred - which
        // is also what took the hand-off below away from the account
        // farm the audit found. Linking does not re-fire the auth-state
        // listener (same user), so the snapshot is refreshed here.
        const linked = credential
          ? await fb.authMod.linkWithCredential(anon, credential)
          : await fb.authMod.linkWithPopup(anon, provider)
        await linked.user.reload()
        await linked.user.getIdToken(true) // next token carries the Google provider + name
        const u = fb.auth.currentUser ?? linked.user
        user.value = { uid: u.uid, name: u.displayName ?? 'Anonymous', photo: u.photoURL ?? null, anonymous: u.isAnonymous }
        await useStardust().refresh()
        await useStarweave().load()
        void useProfile().afterSignIn()
        if (apple) void afterAppleSignIn(fb, apple)
        return
      } catch (e) {
        if ((e as { code?: string })?.code !== 'auth/credential-already-in-use') throw e
        // 🔁 v4.92 fallback: a returning Google player who played
        // signed-out on this device. Mint a one-time transfer code with
        // the still-valid anonymous token, sign in as the existing
        // account, redeem it as that identity. Every step is
        // best-effort: wallet plumbing must never block signing in.
        const failed = e as Parameters<typeof fb.authMod.GoogleAuthProvider.credentialFromError>[0]
        const cred = credential ?? (method === 'apple' ? fb.authMod.OAuthProvider.credentialFromError(failed) : fb.authMod.GoogleAuthProvider.credentialFromError(failed))
        const from = anon.uid
        let transferCode: string | null = null
        try {
          await useStardust().flush()
          const token = await anon.getIdToken()
          const res = await apiFetch<{ code: string }>('/api/wallet/transfer-code', {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
          })
          transferCode = res.code
        } catch (error) {
          console.warn('Wallet transfer code unavailable — signing in anyway.', error)
        }
        if (cred) await fb.authMod.signInWithCredential(fb.auth, cred)
        else await fb.authMod.signInWithPopup(fb.auth, provider)
        const upgraded = fb.auth.currentUser
        if (apple && upgraded) void afterAppleSignIn(fb, apple)
        if (transferCode && upgraded && !upgraded.isAnonymous) {
          try {
            const token = await upgraded.getIdToken()
            const redeemed = await apiFetch<{ transferred: boolean }>('/api/wallet/redeem', {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
              body: { code: transferCode, from, epoch: PROGRESS_EPOCH },
            })
            if (redeemed.transferred) useStardust().transferOutbox(from, upgraded.uid)
            await useStardust().refresh()
            await useStarweave().load()
          } catch (error) {
            console.warn('Wallet transfer redeem failed — the code expires on its own.', error)
          }
        }
      }
    } catch (cause) {
      if (!cancelledSignIn(cause)) {
        error.value = (cause as { code?: string })?.code === 'auth/native-sign-in-timeout'
          ? 'auth.signInTimeout' : 'auth.signInFailed'
        console.warn('Sign-in did not complete.', cause)
      }
    } finally {
      busy.value = false
    }
  }

  async function signOutUser() {
    if (!import.meta.client) return
    const fb = await bootFirebase()
    // 👤 v7.15: the run's last checkpoint ships before the account leaves
    // this device - once ANOTHER account signs in here, the local copy is
    // cleared (useRunSync.onIdentity), so the cloud copy must be current.
    try {
      // v7.18: not fatal - an unshipped run is parked for this account on
      // the next identity change and restored when it signs back in.
      if (!await useRunSync().settle()) console.warn('Run sync could not ship the last checkpoint before sign-out - it stays on this device for this account.')
    } catch (error) {
      console.warn('Run sync did not settle before sign-out.', error)
    }
    // ☁️ v7.88: the meta progress too - codex, achievements, unlocks,
    // favorites, puzzle bests. The next Google account to sign in here
    // clears the device's copy before its own pull (useCloudSync), so this
    // account's last change has to be in its cloud document first. Not
    // fatal either: a failed push leaves the change on this device until
    // the next push, or until another account takes the device over.
    try {
      if (!await useCloudSync().settle()) console.warn('Cloud sync could not ship the last progress before sign-out.')
    } catch (error) {
      console.warn('Cloud sync did not settle before sign-out.', error)
    }
    // 🏦 v7.88: and queued earnings go to the account that earned them, not
    // to the next one to flush the outbox on this device.
    try {
      await useStardust().flush()
    } catch (error) {
      console.warn('The wallet did not flush before sign-out.', error)
    }
    await fb.authMod.signOut(fb.auth)
    // The balance belongs to the account, not the device.
    useStardust().onSignedOut()
    await flushNativePersistence()
  }

  return { user, busy, error, signIn, signOutUser }
}
